import { FC } from 'react';

import { Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconInfo from '@/assets/icons/icon-info.svg?react';
import Table from '@/components/RTable';
import Tooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import { SimulationResult } from '@/types/simulations';
import { formatNumberWithCommas } from '@/utils';

import { StyledTable } from './styles';

interface AncillaryTableDataTypes {
  key: string;
  title: string | JSX.Element;
  unit: string;
  value: number | string;
}

interface AncillaryTableProps {
  simulationData: SimulationResult;
}

const AncillaryTable: FC<AncillaryTableProps> = ({ simulationData }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();

  const ancillaryDataTable: AncillaryTableDataTypes[] = [
    {
      key: '1',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.capacityOfPfc.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.capacityOfPfc.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.capacityOfPfc.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      unit: 'MW',
      value: formatNumberWithCommas(
        Number(simulationData.result.offeredCapacityOfPfc.toFixed(0))
      ),
    },
    {
      key: '2',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.revenueOfPfc.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.revenueOfPfc.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.revenueOfPfc.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      unit: currencySymbol,
      value: formatNumberWithCommas(
        Number(simulationData.result.revenueFromOfferedCapacityOfPfc.toFixed(0))
      ),
    },
    {
      key: '3',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.capacityOfSfc.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.capacityOfSfc.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.capacityOfSfc.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      unit: 'MW',
      value: formatNumberWithCommas(
        Number(simulationData.result.offeredCapacityOfSfc.toFixed(0))
      ),
    },
    {
      key: '4',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.revenueOfSfc.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.revenueOfSfc.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.ancillaryServices.revenueOfSfc.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      unit: currencySymbol,
      value: formatNumberWithCommas(
        Number(simulationData.result.revenueFromOfferedCapacityOfSfc.toFixed(0))
      ),
    },
  ];

  const ancillaryColumns = [
    {
      dataIndex: 'title',
      width: '28%',
    },
    {
      title: (
        <Flex justify="center" align="center" gap={8}>
          <Typography.Text className="fs-14-bold">
            {t('result.finance.incomeBreakdown.tables.columns.value')}
          </Typography.Text>
        </Flex>
      ),
      dataIndex: 'unit',
      key: 'unit',
      width: '36%',
    },
    {
      title: (
        <Flex justify="center" align="center" gap={8}>
          <Typography.Text className="fs-14-bold">
            {t('result.finance.incomeBreakdown.tables.columns.earnedAmount')}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.columns.earnedAmount'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.columns.earnedAmountDescription'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      dataIndex: 'value',
      key: 'value',
      width: '36%',
    },
  ];
  return (
    <StyledTable>
      <Table
        dataSource={ancillaryDataTable}
        columns={ancillaryColumns}
        pagination={false}
      />
    </StyledTable>
  );
};

export default AncillaryTable;
